import { memo } from "react";
import Markdown, { type Components, type Options } from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatCodesSafely } from "@/lib/codes.ts";
import { cn } from "@/lib/utils.ts";

type MdNode = { type: string; value?: string; children?: MdNode[] };

/** Groups 10-digit UKTZED codes (`formatCodesInText`) in text nodes only, so link targets keep their digits. */
function remarkUktzedCodes() {
  return (tree: MdNode) => {
    const walk = (node: MdNode) => {
      if ((node.type === "text" || node.type === "inlineCode") && typeof node.value === "string") {
        node.value = formatCodesSafely(node.value);
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

const plugins: Options["remarkPlugins"] = [remarkGfm, remarkUktzedCodes];

const components: Components = {
  a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
  table: ({ node: _node, ...props }) => <table {...props} />,
};

/** Markdown written by the agent: GFM, links in a new tab, codes grouped as `8482 10 90 00`. */
export const MarkdownText = memo(function MarkdownText({
  text,
  className,
  size = "default",
  streaming = false,
}: {
  text: string;
  className?: string;
  size?: "default" | "sm";
  /** Shows a blinking caret after the last word while the reply is still being written. */
  streaming?: boolean;
}) {
  return (
    <div className={cn("markdown", size === "sm" && "markdown-sm", streaming && "markdown-streaming", className)}>
      <Markdown remarkPlugins={plugins} components={components}>
        {text}
      </Markdown>
    </div>
  );
});
